import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as Rx from 'rxjs';
import { FormBuilder, FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { FirestoreAccess} from "src/lib/firestore-access";
import { MagliveApiClient } from "src/lib/api-client/maglive-api-client";
import {
  Live,
  LiveThumbnail,
  LiveUpdateRequest,
  MagazineNgWord
} from "src/lib/entity/live";
import { Room } from "src/lib/entity/room";
import { environment } from "src/env/environment";
import { map, concatMap, switchMap, mergeMap, catchError, tap, toArray } from 'rxjs/operators';
import { ToastrService, ActiveToast } from 'ngx-toastr';
import { containNgWordValidator } from 'src/app/parts/validation/admin/ngWordValidator.directive';
import { MagliveUser } from 'src/lib/entity/user';
import { getUniqueStr } from 'src/lib/util';

@Component({
  selector: 'admin-archive-edit',
  templateUrl: './archive-edit.component.html',
  styleUrls: ['../../../../pages/admin/startbootstrap-sb-admin-gh-pages/src/scss/styles.scss', './archive-edit.component.scss']
})
export class ArchiveEditComponent implements OnInit {

  /**
   * アップロード上限 18GB
   * ECSのエフェメラルストレージのデフォルト上限が20GBであり、-2GB余裕を持たせている。
   */
  private MAX_FILE_SIZE = 19327352832;
  
  /**
   * 分割アップロードの各チャンクの最大ファイルサイズ 500MB
   */
  private FILE_CHUNK_SIZE = 50_000_000;

  /**
   * マルチパートアップロードの固定処理数。
   * プログレスバーの割合計算で使用する。処理単位は以下の３つとする
   * 1. マルチパートアップロード開始
   * 2. 署名付きURLの発行
   * 3. マルチパートアップロード終了
   * 
   * 署名付きURLによる実際のアップロード処理数は含めない。
   * 後続のチャンクファイル分割処理時に、算出したチャンク数を合算する。
   */
  private UPLOAD_PROCESS_COUNT = 3;

  /**
   * オブジェクトアップロードの最大並列数 15個
   */
  private MAX_UPLOAD_PARALLELS_COUNT = 15
  
  /**
   * クリック無効秒数 3秒
   */
  private CLICK_INVALID_SECONDS = 3000

  public defaultThumbnailUrl: string;

  // フォーム関係
  formGroup: FormGroup;
  isWaiting: boolean;
  invalidThumbnailSize: boolean;
  private paramMagazineId: number;
  

  // ライブ情報
  private liveId: string;
  private live: Live;
  live$: Rx.Observable<Live>;
  private selectedThumbnailFile: File;

  // Ngワード情報
  private allNgWordList: string[];

  // ルーム情報
  room: Room;

  // クリエイター情報
  creator: MagliveUser;
  private liveCreatorComment: string;
  /**
   * マルチパートアップロードの実行ID
   * マルチアップロード開始時に発行される。以下の時に使用する
   * 1. 署名付きURL発行時
   * 2, マルチアップロード終了時
   * https://docs.aws.amazon.com/ja_jp/AmazonS3/latest/userguide/mpuoverview.html
   */
  private uploadId: string;
  // 選択中のアップロードファイル
  selectedArchiveFile: File;
  // アップロード先のS3オブジェクトのパス
  private objectPath: string;
  // アップロードファイルの拡張子
  private objectFileType: string;
  // マルチパートアップロードのパーツ数
  private chunkCount = 0
  // 全てのアップロード処理数
  private totalProcess = 0;
  // 進行中のアップロード処理数
  private processCount = 0;
  // アップロード進行状況のパーセンテージ
  private processPercent = 0;
  // トースター表示用のオブジェクト
  private toastObj: ActiveToast<any>

  private subscription: Rx.Subscription;

  constructor(
    private fb: FormBuilder,
    private firestoreAccess: FirestoreAccess,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.route.params.subscribe(params => {
      this.liveId = params['id']
      this.paramMagazineId = params['magazineId'] ? parseInt(params['magazineId'], 10) : null
    });
  }

  ngOnInit(): void {
    if(!this.liveId) {
      this.cancelEditLive();
    } else {
      if (environment.name === "local") {
        this.defaultThumbnailUrl = 'https://live.stg.mag2.com/assets/images/thumbnail.png';
      } else {
        this.defaultThumbnailUrl = environment.baseURL.magliveWeb + 'assets/images/thumbnail.png';
      }

      this.createForm();

      this.firestoreAccess.getCreatorComment(this.liveId).subscribe((x) => {
        this.liveCreatorComment = x.length > 0 ? x[0].message : "";
        this.formGroup.patchValue({
          creatorComment: this.liveCreatorComment
        });
      });

      this.firestoreAccess.getLive(this.liveId).pipe(
        switchMap(live => {
          if(live != null) {
            this.live = live;
            const live$ = Rx.of(live);
            this.live$ = live$

            return live$
          }
          return Rx.EMPTY;
        }),
        mergeMap(live => {
          if(live != undefined) this.getCreatorByUserId(live.userId);
          return Rx.of(live);
        }),
        mergeMap(live => {
          if(live != undefined) this.getNgWord(live.magazineId);
          return Rx.of(live);
        }),
        mergeMap(live => {
          if(live != undefined && live.roomId != undefined) {
            return this.firestoreAccess.getRoom(live.roomId).pipe(
              tap(room => {
                this.room = room;
              }),
            )
          }
          return Rx.of(live);
        }),
        tap(_ => {
          this.configureFormGroup()
        })
      ).subscribe()
    }
  }

  get title() { return this.formGroup.get('title'); }
  get description() { return this.formGroup.get('description'); }
  get viewerSegment() { return this.formGroup.get('viewerSegment'); }
  get isScreenRotated() { return this.formGroup.get('isScreenRotated'); }
  get isViewerCountEnabled() { return this.formGroup.get('isViewerCountEnabled') as FormArray; }
  get isArchived() { return this.formGroup.get('isArchived'); }
  get isLimitedTimeRelease() { return this.formGroup.get('isLimitedTimeRelease'); }
  get archiveType() { return this.formGroup.get('archiveType'); }
  get archiveDays() { return this.formGroup.get('archiveDays'); }
  get thumbnailPath() { return this.formGroup.get('thumbnailPath'); }
  get thumbnailURL() { return this.formGroup.get('thumbnailURL'); }
  get archiveFile() { return this.formGroup.get('archiveFile'); }
  get creatorComment() { return this.formGroup.get('creatorComment'); }

  /**
   * リアクティブフォームを構築する
   * @return void
   */
  private createForm() {
    this.formGroup = this.fb.group({
      title: new FormControl(null, [
        Validators.required,
        Validators.maxLength(40),
      ]),
      description: new FormControl(null, Validators.maxLength(140)),
      viewerSegment: new FormControl(null, Validators.pattern("ALL|SUBSCRIBER")),
      isScreenRotated: new FormControl(null),
      isViewerCountEnabled: new FormControl(null, Validators.pattern("true|false")),
      isArchived: new FormControl(null),
      isLimitedTimeRelease: [false, Validators.pattern("true|false")],
      archiveType: new FormControl(null),
      archiveDays: new FormControl(null, [Validators.min(1)]),
      thumbnailPath: new FormControl(null),
      thumbnailURL: new FormControl(null),
      archiveFile: new FormControl(null, Validators.required),
      creatorComment: new FormControl(null, Validators.maxLength(140)),
    });
  }

  /**
   * リアクティブフォームを初期化
   * @return void
   */
  private configureFormGroup(): void {
    // データ整形
    const isConverted = this.room?.convert_hls_status === "success"
    const archiveType = this.getArchiveType(isConverted)

    // 編集フォームの初期値を設定
    this.formGroup.patchValue({
      title: this.live.title,
      description: this.live.description,
      viewerSegment: this.live.options.viewerSegment,
      isScreenRotated: this.live.options.isScreenRotated,
      isViewerCountEnabled: this.live.options.isViewerCountEnabled,
      isArchived: this.live.options.isArchived,
      isLimitedTimeRelease: this.live.options.archiveDays > 0,
      archiveType: archiveType,
      archiveDays: this.live.options.archiveDays > 0 ? this.live.options.archiveDays : null,
      thumbnailPath: this.live.thumbnailPath,
      thumbnailURL: this.live.thumbnailURL,
    });

    // マガジンIDに紐づくNGワードとグローバルNGワードをフロントバリデーションにセットする
    this.title.setValidators([containNgWordValidator(this.allNgWordList),Validators.required, Validators.maxLength(40)]);
    this.description.setValidators([containNgWordValidator(this.allNgWordList), Validators.maxLength(140)]);
    this.creatorComment.setValidators([containNgWordValidator(this.allNgWordList), Validators.maxLength(140)]);
    this.live.archiveUploadStatus === "INIT" ? this.archiveFile.setValidators([ Validators.required ]) : this.archiveFile.setValidators([]);
    this.archiveFile.updateValueAndValidity();

    if(archiveType == "limited_time_release") {
      this.formGroup.controls.archiveDays.enable();
    } else {
      this.formGroup.controls.archiveDays.disable();
    }

    this.archiveDays.updateValueAndValidity();
  }

  private getArchiveType(isConverted: boolean): string {
    /**
     * アーカイブアップロード完了後
     * 既存のアーカイブ設定の条件判定と同じ
     */
    if(isConverted) { 
      if(!this.live.options.isArchived) return "private";
      if(this.live.options.isArchived === true && this.live.options.archiveDays !== null && this.live.options.archiveDays !== 0) {
        // 公開期限日が過ぎた場合、非公開に設定する
        if(this.live.startedAt !== null) {
          const startedAtConvertedInMillisecond = this.live.startedAt.seconds * 1000;
          const ahciveDaysConvertedInMillisecond = this.live.options.archiveDays * 60 * 60 * 24 * 1000;
          const millsecondsAddedStartedAtToArchiveDays = startedAtConvertedInMillisecond + ahciveDaysConvertedInMillisecond
  
          if(new Date().getTime() - millsecondsAddedStartedAtToArchiveDays > 0) {
            return "private";
          }
        }
        return "limited_time_release"
      }
      return "public";
    }

    /**
     * アーカイブアップロード完了前
     * 常にisArchivedがfalseになる為、以下のような条件でアーカイブ設定の状態を判定する
     * 
     * isArchiedがfalse 且つ archiveDays が １日以上 -> 期間限定公開
     * isArchiedがfalse 且つ archiveDays が false -> 非公開
     * それ以外 -> 公開（基本的に公開状態になることはない）
     */
    
    if(!this.live.options.isArchived && this.live.options.archiveDays > 0){
      return "limited_time_release"
    } else if(!this.live.options.isArchived && !this.live.options.archiveDays) {
      return "private";
    } else {
      return "public";
    }
  }

  private getCreatorByUserId(userId: number): void {
    this.firestoreAccess.getUser(userId)
      .subscribe( creator => {
        this.creator = creator;
      })
  }

  private getNgWord(magazineId?: number) {
    this.allNgWordList = [];
    MagliveApiClient.listGlobalNgWords()
    .subscribe( x => {
      if(x !== "NOT_AUTHORIZED") {
        x.map(ngWord => {
          this.allNgWordList.push(ngWord.word);
        })
      }
    })

    if (magazineId) {
      this.firestoreAccess.getNgwords(magazineId)
      .subscribe(x => {
        x.map(ngWord=> {
          let magazineNgWord = ngWord as MagazineNgWord;
          this.allNgWordList.push(magazineNgWord.word);
        })
      })
    }
  }

  private cancelEditLive(): void {
    this.router.navigate(['admin/lives'])
  }

  changeViewerCountCheckbox(event: any) {
    this.formGroup.patchValue({
      isViewerCountEnabled: !event.target.checked
    });
  }

  changeArchiveType(value: string): void {
    switch(value) {
      case 'limited_time_release':
        this.formGroup.controls.archiveDays.enable();
        this.formGroup.patchValue({
          isArchived: true,
          archiveDays: this.live.options.archiveDays > 0 ? this.live.options.archiveDays : 1
        });
        this.archiveDays.setValidators([Validators.required, Validators.min(1)]);
        break;
      case 'public':
        this.formGroup.controls.archiveDays.disable();
        this.formGroup.patchValue({
          isArchived: true,
          archiveDays: null
        });
        this.archiveDays.setValidators([Validators.min(1)]);
        break;
      default:
        this.formGroup.controls.archiveDays.disable();
        this.formGroup.patchValue({
          isArchived: false,
          archiveDays: null
        });
        this.archiveDays.setValidators([Validators.min(1)]);
    }
    this.archiveDays.updateValueAndValidity();
  }

  changeArchiveCheckBox(event: any) {
    if(event.target.checked) {
      this.formGroup.controls.archiveDays.enable();
      this.formGroup.patchValue({
        isLimitedTimeRelease: true,
        archiveDays: this.live.options.archiveDays > 0 ? this.live.options.archiveDays : 1,
      });
      this.archiveDays.setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.formGroup.controls.archiveDays.disable();
      this.formGroup.patchValue({
        isLimitedTimeRelease: false,
        archiveDays: null
      });
      this.archiveDays.setValidators([Validators.min(1)]);
    }
    this.archiveDays.updateValueAndValidity();
  }

  clickThumbnailButton(event: any): void {
    event.target.value = '';
  };

  changeThumbnail(event: any): void {
    const reader = new FileReader()
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.selectedThumbnailFile = file;

      reader.onload = (() => {
        let image = new Image;
        image.src = reader.result as string;
        image.onload = () => {
          const factor = Math.abs(image.width / image.height - 16.0 / 9.0);
          const allowed = 0.015;
          this.invalidThumbnailSize = allowed <= factor;
        }
        this.formGroup.patchValue({
          thumbnailURL: reader.result
        });
      })
      reader.readAsDataURL(file);
    }
  }

  removeSelectingThumbnail(): void {
    if(this.thumbnailURL.value !== this.defaultThumbnailUrl) {
      this.selectedThumbnailFile = null;
      this.invalidThumbnailSize = false;
      this.formGroup.patchValue({
        thumbnailPath: null,
        thumbnailURL: this.defaultThumbnailUrl
      });
    }
  }

  clickArchiveFileButton(event: any): void {
    event.target.value = '';
  };


  changeArchiveFile(event: any): void {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.selectedArchiveFile = file;
      let fileSize = file.size
      if(fileSize > this.MAX_FILE_SIZE) {
        this.archiveFile.setErrors({ isMaxFileSize: true});
      }

      this.calcChunkCount(fileSize)
      this.calcAspectRatio(file)
    }
  }

  /**
   * 選択された動画のチャンク数を計算する
   * @param fileSize 
   */
   private calcChunkCount(fileSize: number) {
    this.chunkCount = Math.ceil(fileSize / this.FILE_CHUNK_SIZE)
  }

  /**
   *  選択された動画の縦横比を算出する
   * @param file 
   */
  private calcAspectRatio(file : File) {
    const videoElement = document.createElement( "video" );
    videoElement.src = URL.createObjectURL(file);
    videoElement.onloadedmetadata = () => {
      this.formGroup.patchValue({
        isScreenRotated: videoElement.videoWidth > videoElement.videoHeight,
      });
    }
  }

  removeSelectingArchiveFile(): void {
    if(this.selectedArchiveFile) {
      this.selectedArchiveFile = null;
      this.formGroup.patchValue({
        archiveFile: null
      });
      this.chunkCount = 0
    }
  }

  /**
   * プログレスバーの割合計算およびトースターを更新する
   * @param toast 
   */
  private updateProgressMeter(toast: ActiveToast<any>) {
    this.processCount++;
    this.processPercent =
      this.totalProcess > 0 ? Math.round((this.processCount / this.totalProcess) * 100) : 0;
    toast.toastRef.componentInstance.message = `アップロード中... ${this.processPercent}%`;
  }

  /**
   * 選択された動画をアーカイブライブとしてアップロードまたは更新する
   */
  public updateArchive() {
    if(this.isWaiting) return;
    this.isWaiting = true;

    // サムネイル登録/削除処理
    const thumbnail$ = this.postThumbnail(this.selectedThumbnailFile)

    const live$ = this.updateLive(thumbnail$)

    const uploadObject$ = this.uploadObject(live$)
      
    this.subscription = this.convertMp4ToHls(uploadObject$).subscribe((x) => {
      if(x !== "CANNOT_CONVERT" && x && x?.convert_hls_status === "progress") return

      setTimeout( () => {
        this.isWaiting = false;
        this.onBack()
        this.subscription.unsubscribe()
      }, this.CLICK_INVALID_SECONDS);
      this.toastr.success("更新が完了しました")
    },
    error => {
      console.error(error)
      setTimeout( () => {this.isWaiting = false;}, this.CLICK_INVALID_SECONDS);
      this.toastr.error("更新に失敗しました")
    })
  }

  /**
   * サムネイル画像を登録または削除する
   * @param file 
   * @returns
   */
  private postThumbnail = (file: File): Rx.Observable<LiveThumbnail | "NOT_AUTHORIZED"> => {
    let thumbnail$ = Rx.of<"NOT_AUTHORIZED" | LiveThumbnail>({
      thumbnailPath: this.thumbnailPath.value,
      thumbnailURL: this.thumbnailURL.value
    })

    if(this.thumbnailURL.value !== this.live.thumbnailURL) {
      let shouldRegisterThumbnail = false;
      let shouldDeleteThumbnail = false;

      if((!this.live.thumbnailURL || this.live.thumbnailURL === this.defaultThumbnailUrl ) && this.thumbnailURL.value && this.thumbnailURL.value !== this.defaultThumbnailUrl) { // 未設定->登録
        shouldRegisterThumbnail = true;
      } else if((this.live.thumbnailURL && this.live.thumbnailURL !== this.defaultThumbnailUrl) && (!this.thumbnailURL.value || this.thumbnailURL.value === this.defaultThumbnailUrl)) { // 登録済み->未設定
        shouldDeleteThumbnail = true;
      } else { // 登録済み->別のサムネイルに変更
        shouldDeleteThumbnail = true;
        shouldRegisterThumbnail = true;
      }

      if(shouldDeleteThumbnail) {
        thumbnail$ = MagliveApiClient.deleteLiveThumbnail(this.live.magazineId, encodeURIComponent(this.live.thumbnailPath))
        .pipe(
          map(() => {
            return {
              thumbnailPath: null,
              thumbnailURL: this.defaultThumbnailUrl
            }
          }),
          catchError(err => {
            return Rx.throwError(err);
          })
        )
      }

      if(shouldRegisterThumbnail && this.selectedThumbnailFile) {
        const formData = new FormData();
        formData.append('file', this.selectedThumbnailFile);

        thumbnail$ = MagliveApiClient.createLiveThumbnail(
          this.live.magazineId,
          formData
        ).pipe(
          map(response => {
            const thumbnailResult = response as LiveThumbnail;
            this.formGroup.patchValue({
              thumbnailPath: thumbnailResult.thumbnailPath,
              thumbnailURL: thumbnailResult.thumbnailURL
            });
            return thumbnailResult
          }),
          catchError(err => {
            return Rx.throwError(err);
          })
        )
      }
    } 
    return thumbnail$
  }

  /**
   * アーカイブライブを更新する
   * @param thumbnail$ 
   * @returns
   */
  private updateLive = (thumbnail$: Rx.Observable<LiveThumbnail | "NOT_AUTHORIZED">) => thumbnail$.pipe(
    concatMap(thumbnail => {
      const thumbnailResult = thumbnail as LiveThumbnail;
      const params: LiveUpdateRequest = {
        userId: this.live.userId,
        magazineId: this.live.magazineId,
        title: this.title.value,
        description: this.description.value,
        thumbnailPath: thumbnailResult.thumbnailPath && thumbnailResult.thumbnailURL !== this.defaultThumbnailUrl ? thumbnailResult.thumbnailPath : null,
        options: {
          broadcastBy: null,
          isScreenRotated: this.isScreenRotated.value,
          isViewerCountEnabled: this.isViewerCountEnabled.value,
          secondsViewWithoutAuthority: this.live.options.secondsViewWithoutAuthority,
          viewerSegment: this.viewerSegment.value,
          commentSegment: this.live.options.commentSegment,
          reactionSegment: this.live.options.reactionSegment,
          isArchived: this.isArchived.value,
          archiveDays: this.archiveDays.value ?? 0,
          isRadioMode: null,
        },
      }

      return MagliveApiClient.updateArchive(this.live.id, params).pipe(
        catchError((err) => {
          return Rx.throwError(err);
        }),
      )
    }),
    concatMap(() => {
      if(this.liveCreatorComment === this.creatorComment.value) return Rx.of(void 0);
      const params = {
        userId: this.live.userId,
        message: this.creatorComment.value,
        device: null
      }

      return MagliveApiClient.createCreatorComment(this.live.id, params).pipe(
        catchError((err)=> {
          return Rx.throwError(err);
        })
      )
    }),
  )

  /**
   * 動画ファイルをS3にアップロードする
   * @param live$ 
   * @returns 
   */
  private uploadObject = (live$: Rx.Observable<string>): Rx.Observable<void | "CANNOT_UPLOAD"> => live$.pipe(
    switchMap(_ => {
      if(this.live.archiveUploadStatus === "INIT" && this.selectedArchiveFile || (this.live.archiveUploadStatus === 'COMPLETED' && this.live.roomId == null)) {
        this.toastObj = this.toastr.info(`アップロード中... ${this.processPercent}%`, null, {
          disableTimeOut: true,
          tapToDismiss: false
        });

        this.totalProcess += this.chunkCount + this.UPLOAD_PROCESS_COUNT;
        this.objectFileType = this.selectedArchiveFile.name.split('.').pop();
        this.objectPath = getUniqueStr() + "/index." + this.objectFileType

        // 1. マルチパートアップロード開始処理
        const uploadId$ = MagliveApiClient.createMultipartUpload(
          this.liveId,
          this.objectPath
        ).pipe(
          map(x => x.uploadId),
          tap(_ => {
            this.updateProgressMeter(this.toastObj)
          }),
          catchError(err => {
            return Rx.throwError(err);
          })
        )

        // 2. 署名付きURLを発行する（オブジェクトをアップロードするエンドポイント）
        const urlList$ = uploadId$.pipe(
          switchMap((uploadId) => {
            this.uploadId = uploadId
            return MagliveApiClient.createPreSignedUrls(
              uploadId,
              this.objectPath,
              this.chunkCount
            ).pipe(
              tap(_ => this.updateProgressMeter(this.toastObj)),
              catchError(err => {
                return Rx.throwError(err);
              })
            )
          })
        )

        // 3. 動画ファイルを分割し、パーツ毎に署名付きURLでアップロードする
        const multipartsEtags$ = urlList$.pipe(
          switchMap(urlList => {
            let offset = 0;
            return Rx.from(urlList).pipe(
              // 並列で最大15個ずつアップロード
              mergeMap((urlItem) => {
                // 動画をチャンクファイルに分割する
                const chunkData$ = Rx.from(
                  new Promise((resolve) => {
                    let reader = new FileReader();
                    reader.onload = (e: any) => {
                      const data = new Uint8Array(e.target.result);
                      resolve(data)
                      reader.abort();
                    }
                    const slice = this.selectedArchiveFile.slice(offset, offset + this.FILE_CHUNK_SIZE, this.selectedArchiveFile.type);
                    offset += this.FILE_CHUNK_SIZE;
                    reader.readAsArrayBuffer(slice);
                    
                  }) as Promise<Uint8Array>
                ).pipe(
                  catchError(err => {
                    return Rx.throwError(err);
                  })
                )

                return chunkData$.pipe(
                  concatMap(data => {
                    // チャンクファイルをアップロードする
                    return Rx.from(
                      MagliveApiClient.uploadPreSignedUrl(urlItem.url, data)).pipe(
                        map((x) => { 
                          return {
                            partNumber: urlItem.partNumber,
                            etag: x
                          }
                        }
                      )
                    )
                  }),
                  catchError(err => {
                    return Rx.throwError(err);
                  })
                )
              }, this.MAX_UPLOAD_PARALLELS_COUNT),
              tap(_ => this.updateProgressMeter(this.toastObj)),
              toArray(),
              catchError(err => {
                return Rx.throwError(err);
              })
            )
          })
        )
          
        // 4. マルチパートアップロード完了処理
        return multipartsEtags$.pipe(
          map(etags => {
            const multipartList = []
            for (let i = 0; i < etags.length; i++) {
              multipartList.push({
                ETag: etags[i].etag,
                partNumber: etags[i].partNumber,
              });
            }
            return multipartList
          }),
          concatMap(multipartList => {
            return MagliveApiClient.completeMultipartUpload(
              this.liveId,
              this.uploadId,
              this.objectPath,
              multipartList
            )
          }),
          tap(_ => {
            this.updateProgressMeter(this.toastObj)
            this.toastr.clear(this.toastObj.toastId);
            this.processCount = 0;
            this.processPercent = 0;
            this.totalProcess = 0;
          }),
          catchError(err => {
            if(this.live.archiveUploadStatus === "PROGRESS") {
              this.resetUploadStatus(this.liveId)
            }
            this.toastr.clear(this.toastObj.toastId);
            this.processCount = 0;
            this.processPercent = 0;
            this.totalProcess = 0;
            return Rx.throwError(err);
          })
        )
      }

      return Rx.of("CANNOT_UPLOAD" as "CANNOT_UPLOAD")
    })
  )

  /**
   * アップロードしたS3オブジェクト（mp4）をHLS形式に変換する
   * @param objectUpload$ 
   * @returns 
   */
  private convertMp4ToHls = (objectUpload$: Rx.Observable<void | "CANNOT_UPLOAD">) => {
    return objectUpload$.pipe(
      tap(x => {
        if(x !== "CANNOT_UPLOAD"){
          this.toastObj = this.toastr.info(`変換中... `, null, {
            disableTimeOut: true,
            tapToDismiss: false
          });
        }
      }),
      concatMap(x => {
        if(x === "CANNOT_UPLOAD") return Rx.of("CANNOT_CONVERT" as "CANNOT_CONVERT")
        return MagliveApiClient.convertArchive(this.liveId, this.objectPath, this.objectFileType).pipe(
          map(x => x.roomId)
        )
      }),
      concatMap(x => {
        if(x == "CANNOT_CONVERT") return Rx.of(x as "CANNOT_CONVERT")
        return this.firestoreAccess.getRoom(x).pipe(
          map(room => {
            if(room) {
              // 変換成功または失敗時（変換Lambdaの内部エラー）、トースターを削除する
              if(room.convert_hls_status == "success" || room.convert_hls_status == "failed") {
                this.toastr.clear(this.toastObj.toastId);
              }
            }
            return room
          })
        )
      }),
      catchError(err => {
        this.toastr.clear(this.toastObj.toastId);
        return Rx.throwError(err);
      })
    )
  }

  onBack(): void {
    if(this.paramMagazineId) {
      this.router.navigate(['admin/creators', this.paramMagazineId, 'lives'], {queryParams: {filter: "upload"}})
    } else {
      this.router.navigate(['admin/lives'], {queryParams: {filter: "upload"}})
    }
  }

  /**
   * ライブのアップロードステータスを初期化する
   * @param liveId 
   */
  private resetUploadStatus(liveId: string) {
    MagliveApiClient.updateArchiveUploadStatusByFetch(liveId, "INIT")
  }

  /**
   * ブラウザを閉じる前・リロード前に、アップロード中のライブがある場合に確認ダイアログを出す
   * @param e イベント
   */
  @HostListener('window:beforeunload', ['$event'])
  beforeunloadHandler(e: Event) {
    if (this.isWaiting) {
      /**
       * アップロード中の条件判定
       * 1. ライブのアップロードステータスが「アップロード中」 且つ まだライブにroomIdが紐付いていない
       * 2. ライブアップロードステータスが「完了」且つ まだライブにroomIdが紐付いていない
       */
      if((this.live?.archiveUploadStatus === "PROGRESS" && this.live?.roomId == null) || (this.live?.archiveUploadStatus === "COMPLETED" && this.live?.roomId == null)){
        e.returnValue = true;
      }
    }
  }

  /**
   * 上記の確認ダイアログでOKとした場合、アップロード中である全ライブのアップロードステータスを初期化する
   */
  @HostListener('window:unload', ['$event'])
  unloadHandler() {
    /**
     * アップロード中の条件判定
     * 1. ライブのアップロードステータスが「アップロード中」 且つ まだライブにroomIdが紐付いていない
     * 2. ライブアップロードステータスが「完了」且つ まだライブにroomIdが紐付いていない
     */
    if((this.live?.archiveUploadStatus === "PROGRESS" && this.live?.roomId == null) || (this.live?.archiveUploadStatus === "COMPLETED" && this.live?.roomId == null)){
      this.resetUploadStatus(this.liveId)
    }
  }
}
